/**
 * ContextAutocomplete Component
 * Reusable autocomplete/combobox for selecting context links
 * Supports search, filtering, and smart suggestions
 */

import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function ContextAutocomplete({
  options = [],
  value = '',
  onChange,
  onSelect,
  placeholder = 'Search...',
  loading = false,
  disabled = false,
  className = '',
  showIcon = false,
  emptyMessage = 'No options found',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setFilteredOptions(options);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = options.filter(option => 
      option.name.toLowerCase().includes(term)
    );
    
    setFilteredOptions(filtered);
    setHighlightedIndex(0);
  }, [searchTerm, options]);

  // Update search term when value changes externally
  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        // Scroll highlighted item into view
        if (dropdownRef.current) {
          const items = dropdownRef.current.querySelectorAll('[data-option]');
          items[Math.min(highlightedIndex + 1, filteredOptions.length - 1)]?.scrollIntoView({
            block: 'nearest',
          });
        }
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        if (dropdownRef.current) {
          const items = dropdownRef.current.querySelectorAll('[data-option]');
          items[Math.max(highlightedIndex - 1, 0)]?.scrollIntoView({
            block: 'nearest',
          });
        }
        break;
      
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    setSearchTerm(option.name);
    setIsOpen(false);
    
    // Call callbacks
    if (onChange) {
      onChange(option.name);
    }
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSearchTerm('');
    setIsOpen(false);
    
    if (onChange) {
      onChange('');
    }
    if (onSelect) {
      onSelect(null);
    }
    
    inputRef.current?.focus();
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Input with search icon and dropdown indicator */}
      <div className="relative">
        <Search 
          size={16} 
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        />
        
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled || loading}
          className={cn(
            'w-full pl-9 pr-20 py-2.5 rounded-lg border',
            'bg-white text-gray-900 placeholder-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent',
            'disabled:bg-gray-100 disabled:cursor-not-allowed',
            'transition-all'
          )}
        />
        
        {/* Right side icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {loading && (
            <Loader2 size={16} className="text-gray-400 animate-spin" />
          )}
          
          {searchTerm && !loading && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              tabIndex={-1}
            >
              <X size={14} className="text-gray-400" />
            </button>
          )}
          
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            tabIndex={-1}
          >
            <ChevronDown 
              size={16} 
              className={cn(
                'text-gray-400 transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </button>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            'absolute z-50 w-full mt-1 py-1',
            'bg-white rounded-lg shadow-lg border border-gray-200',
            'max-h-60 overflow-y-auto'
          )}
        >
          {loading ? (
            <div className="px-4 py-8 text-center text-gray-500">
              <Loader2 size={20} className="mx-auto mb-2 animate-spin" />
              <div className="text-sm">Loading options...</div>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500 text-sm">
              {emptyMessage}
            </div>
          ) : (
            filteredOptions.map((option, index) => (
              <button
                key={option.id}
                type="button"
                data-option
                onClick={() => handleSelectOption(option)}
                className={cn(
                  'w-full px-4 py-2.5 text-left',
                  'hover:bg-purple-50 transition-colors',
                  'focus:outline-none focus:bg-purple-50',
                  highlightedIndex === index && 'bg-purple-50'
                )}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="flex items-center gap-2">
                  {showIcon && option.icon && (
                    <span className="text-lg">{option.icon}</span>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {option.name}
                    </div>
                    
                    {/* Optional metadata */}
                    {option.amount !== undefined && (
                      <div className="text-xs text-gray-500">
                        ${option.amount?.toFixed(2)}
                        {option.spent !== undefined && (
                          <span className="ml-1">
                            (Spent: ${option.spent?.toFixed(2)})
                          </span>
                        )}
                      </div>
                    )}
                    
                    {option.categoryType && (
                      <div className="text-xs text-gray-500 capitalize">
                        {option.categoryType}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
